import { Plus } from 'lucide-react';
import { Game } from 'modules/games/domain/interfaces/game';
import { StageType } from 'modules/tournaments/domain/enums/stage-type';
import UDTippy from 'ud-ui/components/tippy';
import { getGenerateMatchesErrorMessage } from './generate-matches-error';
import { UpsertGameDto } from 'modules/tournaments/domain/repositories/tourneys-repository';
import { UDText } from 'modules/ud-ui/components';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { AppDispatch } from 'store/store';
import { theme } from 'styles/theme/theme';
import UDButton from 'ud-ui/components/button';
import Modal from 'ud-ui/components/modal';
import { useModal } from 'ud-ui/components/modal-old/use-modal';
import ChooseGroupForm from '../../../../../../games/ui/components/choose-group-game-form/choose-group-form';
import CreateGameForm, { UpsertMatchParams } from '../../../../../../games/ui/components/create-game-form';
import GamesList from '../../../../../../games/ui/components/games-list/games-list';
import { StageV2 } from '../../../../../domain/interfaces/stage';
import { deleteChampionshipGame, upsertChampionshipGame } from '../../../../../store/championship/actions';
import { championshipDataSelector } from '../../../../../store/championship/selectors';
import { selectSelectedStageGroups, stageGameDetailsListSelector, stageProcessingSelector } from '../../../../../store/stages';
import { fetchGameDetails, generateStageMatches } from '../../../../../store/stages/stages.actions';

type StageMatchesProps = {
  tournamentId: number;
  championshipId: number;
  stage: StageV2;
  onShowResultsClick?: (gameId: number) => void;
  onShowTemplatesClick?: (gameId: number) => void;
  onShowMomentsClick?: (gameId: number) => void;
};

const StageMatches = (props: StageMatchesProps) => {
  const { stage, tournamentId, championshipId, onShowTemplatesClick, onShowResultsClick, onShowMomentsClick } = props;

  const dispatch = useDispatch<AppDispatch>();
  const [selectedGame, selectGame] = useState<Game | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const championship = useSelector(championshipDataSelector(championshipId));
  const { isProcessing } = useSelector(stageProcessingSelector);
  const gameDetailsList = useSelector(stageGameDetailsListSelector);

  const groups = useSelector(selectSelectedStageGroups);

  const { open, close, Modal: CreateModal } = useModal({ defaultShow: false });
  const { open: openEdit, close: closeEdit, Modal: EditModal } = useModal({ defaultShow: false });
  const { open: openChooseGroup, close: closeChooseGroup, Modal: ModalChooseGroup } = useModal({ defaultShow: false });
  const [isGenerateConfirmOpen, setIsGenerateConfirmOpen] = useState(false);

  const AUTOGENERATE_SUPPORTED_TYPES = [StageType.ROUND_ROBIN_1, StageType.ROUND_ROBIN_2, StageType.GROUP];
  const isAutoGenerateSupported = AUTOGENERATE_SUPPORTED_TYPES.includes(stage.type as StageType);

  const { teams } = stage;

  const handleGenerateMatchesConfirm = useCallback(() => {
    setIsGenerateConfirmOpen(false);
    dispatch(generateStageMatches(stage.id))
      .unwrap()
      .then(() => {
        toast.success('Матчи успешно сгенерированы');
      })
      .catch((error) => {
        toast.error(getGenerateMatchesErrorMessage(error), {
          autoClose: false,
        });
      });
  }, [dispatch, stage.id]);

  const handleCreateGameClick = useCallback(() => {
    if (stage.type === 'GROUP') {
      openChooseGroup();
    } else {
      open();
    }
  }, [open, openChooseGroup, stage.type]);

  const handleEditGameClick = useCallback(
    (game: Game) => {
      if (!gameDetailsList?.byId[game.id]) {
        dispatch(fetchGameDetails({ gameId: game.id, championshipId }));
      }

      selectGame(game);
      openEdit();
    },
    [dispatch, openEdit, gameDetailsList, championshipId],
  );

  const handleCloseEditMatchDialog = useCallback(() => {
    selectGame(null);
  }, []);

  const onShowGameResultClick = useCallback(
    (gameId: number) => {
      if (onShowResultsClick) {
        onShowResultsClick(gameId);
      }
    },
    [onShowResultsClick],
  );

  const onShowGameTemplatesClick = useCallback(
    (gameId: number) => {
      if (onShowTemplatesClick) {
        onShowTemplatesClick(gameId);
      }
    },
    [onShowTemplatesClick],
  );

  const handleDeleteGameClick = useCallback(
    (gameId: number) => {
      dispatch(deleteChampionshipGame({ gameId, championshipId }))
        .then((result) => {
          if (result.payload) {
            toast.success('Матч удален');
          } else {
            toast.error('Ошибка удаления матча');
          }
        })
        .catch(() => {
          toast.error('Ошибка удаления матча');
        });
    },
    [dispatch],
  );

  const handleCreateGameSubmit = useCallback(
    async (values: UpsertMatchParams) => {
      const params = {
        tournamentId,
        championshipId,
        values,
      };

      dispatch(upsertChampionshipGame(params))
        .then((result) => {
          if (result.payload) {
            close();
          } else {
            toast.error('Ошибка создания матча');
          }
        })
        .catch((err) => {
          console.error('create game error:', err);
          toast.error('Ошибка создания матча');
        });
    },
    [tournamentId, championshipId, close, dispatch],
  );

  const handleEditGameSubmit = useCallback(
    async (values: UpsertMatchParams, setSubmitting: (isSubmitting: boolean) => void) => {
      if (!selectedGame) {
        return;
      }

      const gameDetails = gameDetailsList?.byId[selectedGame.id];

      const params: UpsertGameDto = {
        tournamentId,
        championshipId,
        matchId: selectedGame.id,
        values: {
          ...values,
          ...(gameDetails &&
            selectedGame.hasResults && {
              // ...(gameDetails && {
              events: gameDetails?.events,
              result: gameDetails?.result,
              videoUrls: gameDetails?.videoUrls,
            }),
        },
      };

      dispatch(upsertChampionshipGame(params))
        .then((result) => {
          if (result.payload) {
            closeEdit();
          } else {
            toast.error('Ошибка редактирования матча');
          }
        })
        .catch((err) => {
          console.error('update game error:', err);
          toast.error('Ошибка редактирования матча');
        })
        .finally(() => {
          setSubmitting(false);
        });
    },
    [selectedGame, gameDetailsList?.byId, tournamentId, championshipId, dispatch, closeEdit],
  );

  const teamsForCreate = useMemo(() => {
    if (selectedGroupId) {
      return groups.find((group) => group.id === selectedGroupId)?.teams || [];
    }

    return teams;
  }, [groups, selectedGroupId, teams]);

  const handleChooseGroup = useCallback(
    (id: number) => {
      setSelectedGroupId(id);
      closeChooseGroup();
      open();
    },
    [closeChooseGroup, open],
  );

  return (
    <div className="row gy-2">
      <div className="col-12">
        <div className="row">
          <div className="col-6">
            <div className="d-flex flex-row"></div>
          </div>
          <div className={'flex justify-between rounded-lg border border-DarkBlue/20 bg-white py-2'} style={{ paddingInline: 14 }}>
            <div className={'flex-center'}>
              <UDText type={'body-1'} isNew center>
                Добавить новые матчи
              </UDText>
            </div>
            <div className="col-6 d-flex justify-content-end" style={{ gap: '8px' }}>
              <UDTippy
                content={!isAutoGenerateSupported ? 'Данный тип этапа не поддерживает автогенерацию матчей' : undefined}
                placement="top"
              >
                <span style={{ display: 'inline-flex' }}>
                  <UDButton
                    variant="primary"
                    className="d-flex align-items-center"
                    onClick={() => setIsGenerateConfirmOpen(true)}
                    loading={isProcessing}
                    disabled={!isAutoGenerateSupported}
                  >
                    <Plus size={16} className="mr-2" color={theme.colors.DarkBlue} />
                    Автозаполнение
                  </UDButton>
                </span>
              </UDTippy>
              <UDButton variant="dark" className="d-flex align-items-center ml-6" onClick={handleCreateGameClick}>
                {/* <UDIcon name="plus" componentProps={{ className: 'mr-2' }} /> */}
                <Plus size={16} className="mr-2" color={theme.colors.AccentNaplesYellow} />
                Добавить матч
              </UDButton>
            </div>
          </div>
        </div>
      </div>
      <div className="col-12">
        <div className="row">
          <div className="col-12">
            <GamesList
              championshipId={championshipId}
              games={stage.matches}
              activeGameId={selectedGame?.id}
              onEditGameClick={handleEditGameClick}
              onDeleteGameClick={handleDeleteGameClick}
              onShowGameResultClick={onShowGameResultClick}
              onShowGameTemplatesClick={onShowGameTemplatesClick}
              onShowMomentsClick={onShowMomentsClick}
            />
          </div>
        </div>
      </div>
      <div className="col-6"></div>

      <Modal show={isGenerateConfirmOpen} onClose={() => setIsGenerateConfirmOpen(false)}>
        <Modal.Header title="Генерация матчей" />
        <Modal.Body className={'max-w-md'}>
          <UDText isNew type="text-1" center>
            Автозаполнение сгенерирует и перезапишет список матчей в зависимости от типа этапа и выбранных команд. Продолжить?
          </UDText>
        </Modal.Body>
        <Modal.Footer justify="between">
          <UDButton variant="secondary" onClick={() => setIsGenerateConfirmOpen(false)} stretch>
            Отмена
          </UDButton>
          <UDButton variant="primary" onClick={handleGenerateMatchesConfirm} stretch>
            Сгенерировать
          </UDButton>
        </Modal.Footer>
      </Modal>
      <ModalChooseGroup header={{ title: 'Выбор группы', subtitle: '' }}>
        <ChooseGroupForm groups={groups} onClick={handleChooseGroup} />
      </ModalChooseGroup>
      <CreateModal header={{ title: 'Добавление матча', subtitle: '' }}>
        <CreateGameForm
          stageId={stage.id}
          rounds={stage.rounds}
          teams={teamsForCreate}
          requiredRound={true}
          onSubmit={handleCreateGameSubmit}
          availableLocations={championship?.locations || []}
          championshipId={championshipId}
        />
      </CreateModal>
      <EditModal header={{ title: 'Редактирование матча', subtitle: '' }} onClose={handleCloseEditMatchDialog}>
        <CreateGameForm
          stageId={stage.id}
          rounds={stage.rounds}
          teams={teams}
          game={selectedGame!}
          requiredRound={true}
          onSubmit={handleEditGameSubmit}
          availableLocations={championship?.locations || []}
          isLoading={isProcessing}
          championshipId={championshipId}
        />
      </EditModal>
    </div>
  );
};

export default StageMatches;
